/*
 * Thin wrapper over the ui DropdownMenu so toolbars and row menus share one
 * look: a trigger, a portalled menu, and check-marked / destructive items.
 */
import {
  Fragment,
  forwardRef,
  type ButtonHTMLAttributes,
  type FC,
  type ReactNode
} from "react";
import {
  Activity,
  ArrowUpDown,
  Check,
  CreditCard,
  Edit2,
  File,
  FileText,
  Filter,
  Flag,
  Layers,
  MoreHorizontal,
  PenTool,
  PlaneLanding,
  PlaneTakeoff,
  Plus,
  RefreshCw,
  Search,
  Send,
  Shield,
  Tag,
  Trash2,
  Upload,
  Users,
  X,
  type LucideIcon
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/** Legacy Feather icon names still accepted by the table helpers. */
export type KitIconName = `Feather${string}` | "empty" | null;

const KIT_ICONS: Record<string, LucideIcon> = {
  FeatherActivity: Activity,
  FeatherArrowUpDown: ArrowUpDown,
  FeatherCheck: Check,
  FeatherCreditCard: CreditCard,
  FeatherEdit2: Edit2,
  FeatherFile: File,
  FeatherFileText: FileText,
  FeatherFilter: Filter,
  FeatherFlag: Flag,
  FeatherLayers: Layers,
  FeatherMoreHorizontal: MoreHorizontal,
  FeatherPenTool: PenTool,
  FeatherPlaneLanding: PlaneLanding,
  FeatherPlaneTakeoff: PlaneTakeoff,
  FeatherPlus: Plus,
  FeatherRefreshCw: RefreshCw,
  FeatherSearch: Search,
  FeatherSend: Send,
  FeatherShield: Shield,
  FeatherTag: Tag,
  FeatherTrash2: Trash2,
  FeatherUpload: Upload,
  FeatherUsers: Users,
  FeatherX: X
};

export const resolveKitIcon = (
  name: KitIconName | undefined
): LucideIcon | null => (name ? KIT_ICONS[name] ?? null : null);

/** Renders a legacy Feather icon name as its lucide equivalent. */
export const KitIcon: FC<{ name?: KitIconName; className?: string }> = ({
  name,
  className
}) => {
  const Icon = resolveKitIcon(name);
  return Icon ? <Icon className={className} aria-hidden /> : null;
};

export type KitMenuItem = {
  key: string;
  label: ReactNode;
  icon?: KitIconName;
  /** Shows a check mark on the right. */
  selected?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  /** Keep the menu open after selecting (multi-select toggles). */
  keepOpen?: boolean;
  onSelect: () => void;
};

const selectHandler = (item: KitMenuItem) => (event: Event) => {
  if (item.keepOpen) event.preventDefault();
  item.onSelect();
};

type Props = {
  trigger: ReactNode;
  items: KitMenuItem[];
  /** Optional divider after the item with this key. */
  dividerAfter?: string;
  align?: "start" | "end";
  className?: string;
  /** Stop pointer/click events from bubbling to a clickable table row. */
  stopRowClick?: boolean;
};

export const KitMenu: FC<Props> = ({
  trigger,
  items,
  dividerAfter,
  align = "start",
  className,
  stopRowClick = false
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
    <DropdownMenuContent
      side="bottom"
      align={align}
      className={cn("w-auto min-w-50", className)}
      onClick={stopRowClick ? (e) => e.stopPropagation() : undefined}
    >
      {items.map((item) => {
        const Icon =
          resolveKitIcon(item.icon) ?? (item.destructive ? Trash2 : null);
        return (
          <Fragment key={item.key}>
            <DropdownMenuItem
              variant={item.destructive ? "destructive" : "default"}
              disabled={item.disabled}
              onSelect={selectHandler(item)}
            >
              {Icon ? <Icon className="size-3.5" /> : null}
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.selected ? (
                <Check className="ml-auto size-3.5 text-accent-seed" />
              ) : null}
            </DropdownMenuItem>
            {dividerAfter === item.key ? <DropdownMenuSeparator /> : null}
          </Fragment>
        );
      })}
    </DropdownMenuContent>
  </DropdownMenu>
);

type GhostButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> & {
  icon: KitIconName;
  label?: ReactNode;
  ariaLabel: string;
  spinning?: boolean;
};

/**
 * Ghost toolbar button (sort, refresh, "…" row menu). Forwards its ref and
 * rest props so it can be a Radix `Trigger asChild`.
 */
export const ToolbarGhostButton = forwardRef<
  HTMLButtonElement,
  GhostButtonProps
>(function ToolbarGhostButton(
  { icon, label, ariaLabel, className, spinning, ...rest },
  ref
) {
  const Icon = resolveKitIcon(icon);
  return (
    <Button
      ref={ref}
      type="button"
      variant="ghost"
      size={label ? "sm" : "icon-sm"}
      aria-label={ariaLabel}
      className={className}
      {...rest}
    >
      {Icon ? <Icon className={cn(spinning && "animate-spin")} /> : null}
      {label ? (
        <span className="hidden max-w-40 min-w-0 truncate md:inline">
          {label}
        </span>
      ) : null}
    </Button>
  );
});
